import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { activityCsv, activityPath } from '../../core/parity.js';
import { downloadCsv } from './csv.js';

/**
 * Admin user activity for 30 or 90 days.
 * @param {{ host: HTMLElement, dead: () => boolean }} ctx
 */
export function mountActivity(ctx) {
  const state = { loading: true, fatal: '', days: 30, data: null };

  function paint() {
    if (ctx.dead()) return;
    clear(ctx.host);
    if (state.loading) {
      ctx.host.append(skeleton(6));
      return;
    }
    if (state.fatal) {
      ctx.host.append(errorPanel({
        title: 'User activity unavailable',
        message: state.fatal,
        action: button('Try again', { variant: 'primary', onClick: () => load(true) }),
      }));
      return;
    }
    ctx.host.append(body());
  }

  function body() {
    const data = state.data || {};
    return el('div', { class: 'set-stack' },
      el('div', { class: 'set-panel-head' },
        el('div', { class: 'set-form-actions' },
          dayButton(30),
          dayButton(90),
          el('button', { type: 'button', class: 'set-btn', onClick: exportCurrent }, 'Export CSV'))),
      el('div', { class: 'set-stats' },
        stat('Total users', data.total_users),
        stat(`Active (${state.days}d)`, data.active_users),
        stat('Days with sign-ups', (data.signup_trend || []).length),
        stat('Top user', (data.top_users && data.top_users[0] && data.top_users[0].name) || '—')),
      trend('Sign-up trend', data.signup_trend),
      trend('Active users per day', data.active_per_day),
      topUsers(data.top_users || []));
  }

  function dayButton(days) {
    return el('button', {
      type: 'button',
      class: state.days === days ? 'set-btn set-btn-primary' : 'set-btn',
      onClick: () => {
        if (state.days === days) return;
        state.days = days;
        load(true);
      },
    }, `${days} days`);
  }

  async function load(initial) {
    if (initial) {
      state.loading = true;
      state.fatal = '';
      paint();
    }
    const data = await api(activityPath(state.days));
    if (ctx.dead()) return;
    state.loading = false;
    if (!data || data.error) {
      state.fatal = (data && data.error) || 'Could not load user activity';
      state.data = null;
    } else {
      state.fatal = '';
      state.data = data;
    }
    paint();
  }

  function exportCurrent() {
    if (!state.data) {
      toast('Nothing to export', 'error');
      return;
    }
    downloadCsv(`user-activity-${state.days}d.csv`, activityCsv(state.data));
  }

  load(true);
  return { dispose() {} };
}

function stat(label, value) {
  return el('div', { class: 'set-stat' },
    el('strong', {}, value == null || value === '' ? '—' : String(value)),
    el('span', { class: 'set-hint' }, label));
}

function trend(title, rows) {
  const list = rows || [];
  const max = Math.max(1, ...list.map((row) => Number(row.count) || 0));
  return el('section', { class: 'set-panel' },
    el('div', { class: 'set-panel-head' },
      el('div', { class: 'set-panel-title' }, el('span', {}, title))),
    list.length
      ? el('div', { class: 'set-bars' }, list.map((row) => bar(row, max)))
      : empty({ title: 'No data', body: 'Nothing recorded for this period.' }));
}

function bar(row, max) {
  const count = Number(row.count) || 0;
  const width = Math.max(0, Math.round((count / max) * 100));
  const label = String(row.date || '').slice(0, 10);
  return el('div', { class: 'set-bar' },
    el('span', {}, label || '—'),
    el('span', { class: 'set-bar-track', 'aria-hidden': 'true' },
      el('span', { class: 'set-bar-fill', style: `width:${width}%` })),
    el('span', {}, String(count)));
}

function topUsers(rows) {
  return el('section', { class: 'set-panel' },
    el('div', { class: 'set-panel-head' },
      el('div', { class: 'set-panel-title' }, el('span', {}, 'Top users'))),
    rows.length
      ? el('div', { class: 'set-table-wrap' },
        el('table', { class: 'set-table' },
          el('thead', {}, el('tr', {},
            el('th', {}, 'Rank'),
            el('th', {}, 'Name'),
            el('th', {}, 'Email'),
            el('th', {}, 'Actions'))),
          el('tbody', {}, rows.map((row, index) => el('tr', {},
            el('td', {}, String(index + 1)),
            el('td', {}, row.name || '—'),
            el('td', {}, row.email || '—'),
            el('td', {}, String(row.action_count ?? 0)))))))
      : empty({ title: 'No actions', body: 'No user actions recorded for this period.' }));
}
